import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { collection, doc, Firestore, getDoc, getDocs, increment, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { environment } from '../../../environments/environment';
import { getDownloadURL, getStorage, listAll, ref } from 'firebase/storage';

export interface FetchUsersOptions {
  userAuthrId?: string;
  includeFriends?: boolean;
  includeKgData?: boolean;
  invitedUsersSet?: Set<string>;
  excludeUserId?: string;
  excludeTeamId?: string;
}

@Injectable({
  providedIn: 'root'
})

export class UserService {

  private isAdminCache: boolean | null = null;
  private isAuthorityCache: boolean | null = null;
  private isBusinessCache: boolean | null = null;
  private profileImageCache = new Map<string, string>();
  private nameCache = new Map<string, string>();
  private firestore: Firestore = environment.firestore;
  private storage = getStorage();

  constructor(
    private auth: Auth,
  ) { }

  // getter para o ID do utilizador autenticado
  get userAuthId(): string | null {
    return this.auth.currentUser?.uid ?? null;
  }

  // obtem o nome do utilizador autenticado
  async fetchUserAuthName(): Promise<string> {
    if (!this.userAuthId) return '';

    try {
      const userRef = doc(this.firestore, 'users', this.userAuthId);
      const snap = await getDoc(userRef);

      if (!snap.exists()) return '';

      const data = snap.data();
      return data['name'] ?? '';
    }
    catch (error) {
      console.error(error);
      return '';
    }
  }

  async getUserName(userId: string, denormalizedName?: string): Promise<string> {
    if (denormalizedName) {
      return denormalizedName;
    }

    const cached = this.nameCache.get(userId);
    if (cached) {
      return cached;
    }

    const fallback = 'Utilizador Desconhecido';

    try {
      const userRef = doc(this.firestore, 'users', userId);
      const snap = await getDoc(userRef);
      const name = snap.exists() ? (snap.data()['name'] || fallback) : fallback;
      this.nameCache.set(userId, name);
      return name;
    } catch (error) {
      console.error('Erro ao obter nome do utilizador:', error);
      return fallback;
    }
  }

  clearRolesCache(): void {
    this.isAdminCache = null;
    this.isAuthorityCache = null;
    this.isBusinessCache = null;
  }

  // verifica se o utilizador autenticado é admin
  async isUserAuthAdmin(): Promise<boolean> {
    // se já temos cache, usar
    if (this.isAdminCache !== null) {
      return this.isAdminCache;
    }

    return new Promise<boolean>((resolve) => {
      onAuthStateChanged(this.auth, async (user) => {
        if (!user) {
          this.isAdminCache = false;
          resolve(false);
          return;
        }

        try {
          const userRef = doc(this.firestore, 'users', user.uid);
          const snap = await getDoc(userRef);

          const roles: string[] = snap.data()?.['roles'] ?? [];
          this.isAdminCache = roles.includes('Admin');

          resolve(this.isAdminCache);
        } catch {
          this.isAdminCache = false;
          resolve(false);
        }
      });
    });
  }

  // verifica se o utilizador autenticado tem a role Authorities
  async isUserAuthAuthority(): Promise<boolean> {
    // se já temos cache, usar
    if (this.isAuthorityCache !== null) {
      return this.isAuthorityCache;
    }

    return new Promise<boolean>((resolve) => {
      onAuthStateChanged(this.auth, async (user) => {
        if (!user) {
          this.isAuthorityCache = false;
          resolve(false);
          return;
        }

        try {
          const userRef = doc(this.firestore, 'users', user.uid);
          const snap = await getDoc(userRef);

          const roles: string[] = snap.data()?.['roles'] ?? [];
          this.isAuthorityCache = roles.includes('Authorities');

          resolve(this.isAuthorityCache);
        } catch {
          this.isAuthorityCache = false;
          resolve(false);
        }
      });
    });
  }

  async isUserAuthBusiness(): Promise<boolean> {
    // se já temos cache, usar
    if (this.isBusinessCache !== null) {
      return this.isBusinessCache;
    }

    return new Promise<boolean>((resolve) => {
      onAuthStateChanged(this.auth, async (user) => {
        if (!user) {
          this.isBusinessCache = false;
          resolve(false);
          return;
        }

        try {
          const userRef = doc(this.firestore, 'users', user.uid);
          const snap = await getDoc(userRef);

          const roles: string[] = snap.data()?.['roles'] ?? [];
          this.isBusinessCache = roles.includes('Business');

          resolve(this.isBusinessCache);
        } catch (error) {
          this.isBusinessCache = false;
          resolve(false);
        }
      });
    });
  }

  // obtem o nome do municipio do utilizador com a role Authorities
  async getUserMunicipality(): Promise<string> {
    if (!this.userAuthId) return '';

    try {
      const userRef = doc(this.firestore, 'users', this.userAuthId);
      const snap = await getDoc(userRef);

      if (!snap.exists()) return '';

      const data = snap.data();
      return data['municipality'] ?? '';
    }
    catch (error) {
      console.error(error);
      return '';
    }
  }

  // obter totalKg recolhidos e totalKg de CO2 salvos
  async getKgByUser(userId: string): Promise<{ totalKg: number, totalCO2Kg: number }> {
    try {

      const spotsRef = collection(this.firestore, "spots");
      const q = query(spotsRef, where("collectedByUserId", "==", userId));

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return { totalKg: 0, totalCO2Kg: 0 };
      }

      let totalKg = 0;
      let totalCO2Kg = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        totalKg += data['collectedKg'] || 0;
        totalCO2Kg += data['savedCO2Kg'] || 0;
      });

      return {
        totalKg: Number(totalKg.toFixed(2)),
        totalCO2Kg: Number(totalCO2Kg.toFixed(2))
      };

    } catch (error) {
      console.error("Erro ao obter recolhas do utilizador:", error);
      return { totalKg: 0, totalCO2Kg: 0 };
    }
  }

  // obter o numero de locais spotted
  async getSpottedPlacesByUser(userId: string): Promise<number> {
    try {
      const userRef = doc(this.firestore, "users", userId);

      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        return 0;
      }

      return snapshot.data()['spotted'] || 0;
    }
    catch (error) {
      return 0;
    }
  }

  // obter o numero de locais cleaned
  async getSpotsCleanedByUser(userId: string): Promise<number> {

    try {

      const spotsRef = collection(this.firestore, "spots");
      const q = query(
        spotsRef,
        where("collectedByUserId", "==", userId),
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return 0;
      }

      return snapshot.size;
    }
    catch (error) {
      console.error("Erro ao obter recolhas do utilizador:", error);
      return 0;
    }
  }

  // Carrega a foto de perfil
  async getUserProfileImage(userId: string): Promise<string | null> {
    const storageRef = ref(this.storage, `users/${userId}`);
    try {
      const listResult = await listAll(storageRef);
      for (const itemRef of listResult.items) {
        if (itemRef.name === 'user_photo.jpg') {
          return await getDownloadURL(itemRef);
        }
      }
      return null;
    } catch (error) {
      console.error('Erro ao obter imagem do utilizador:', error);
      return null;
    }
  }

  // carrega as imagens de perfil de uma lista de utilizadores
  async loadProfileImages(users: any[]): Promise<void> {
    for (const user of users) {

      // se já existe em cache
      if (this.profileImageCache.has(user.userId)) {
        user.profilePicture = this.profileImageCache.get(user.userId);
        continue;
      }

      const imageRef = ref(
        this.storage,
        `users/${user.userId}/user_photo.jpg`
      );

      try {
        const url = await getDownloadURL(imageRef);
        this.profileImageCache.set(user.userId, url);
        user.profilePicture = url;
      } catch {
        user.profilePicture = null;
      }
    }
  }

  // obter lista de IDs dos amigos
  async getFriendsIds(userId: string): Promise<string[]> {
    const userRef = doc(this.firestore, 'users', userId);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) return [];

    return snapshot.data()['friendsIds'] || [];
  }

  // obter os dados do utilizador pelo id
  async fetchUsersById(userIds: string[]): Promise<any[]> {
    const usersPromises = userIds.map(async (userId) => {
      try {
        const userRef = doc(this.firestore, 'users', userId);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          return null;
        }

        const userData = userSnap.data();

        // gerar iniciais para o avatar
        const name = userData['name'] || 'Utilizador';
        const initials = name
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .substring(0, 2);

        return {
          userId: userId,
          name: name,
          initials: initials,
          profilePicture: null
        };
      } catch (error) {
        console.error('Erro ao obter utilizador:', userId, error);
        return null;
      }
    });

    const users = await Promise.all(usersPromises);
    return users.filter(u => u !== null);
  }

  // obter todos os utilizadores (dados base)
  async fetchAllUsers(options: FetchUsersOptions = {}): Promise<any[]> {
    const usersCollection = collection(this.firestore, 'users');
    const snapshot = await getDocs(usersCollection);

    const users: any[] = [];

    const friendsIds = options.includeFriends && options.userAuthrId
      ? await this.getFriendsIds(options.userAuthrId)
      : [];

    const kgByUserMap = options.includeKgData
      ? await this.getKgByAllUsers()
      : new Map();


    for (const doc of snapshot.docs) {
      const userData = doc.data();
      const userId = doc.id;

      if (options.excludeUserId && userId === options.excludeUserId) continue;
      if (options.excludeTeamId && userData['teamId'] === options.excludeTeamId) continue;

      const fullName = (userData['name'] ?? '').trim();
      if (!fullName) continue; // ignora utilizadores sem nome

      const nameParts = fullName.split(/\s+/);
      const name =
        nameParts.length > 1
          ? `${nameParts[0]} ${nameParts[nameParts.length - 1]}`
          : nameParts[0];

      const initials = name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);

      const kgData = kgByUserMap.get(userId) || { totalKg: 0, totalCO2Kg: 0 };

      users.push({
        userId,
        profilePicture: null,
        name,
        email: userData['email'],
        points: userData['points'] || 0,
        teamId: userData['teamId'],
        roles: userData['roles'] ?? [],
        isMyFriend: options.includeFriends ? friendsIds.includes(userId) : false,
        initials,
        collectedKg: kgData.totalKg,
        savedCO2Kg: kgData.totalCO2Kg,
        alreadyInvited: options.invitedUsersSet?.has(userId) ?? false
      });
    }

    return users;
  }

  // verifica se um utilizador pertence a uma equipa
  async getUserTeam(userId: string, teamId: string): Promise<boolean> {
    try {
      const userRef = doc(this.firestore, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return false;

      const userData = userSnap.data() || {};
      return userData['teamId'] === teamId;
    } catch {
      return false;
    }
  }

  // atualizar permissões de utilizadores
  async updateUserRoles(userId: string, selectedRoles: string[]) {
    try {
      const userRef = doc(this.firestore, 'users', userId);

      await updateDoc(userRef, {
        roles: selectedRoles
      });
    }
    catch (error) {
      console.error(error);
    }
  }

  // função para obter os kg de lixo e C02
  async getKgByAllUsers(): Promise<Map<string, { totalKg: number, totalCO2Kg: number }>> {
    const spotsRef = collection(this.firestore, "spots");
    const snapshot = await getDocs(spotsRef);

    const resultMap = new Map<string, { totalKg: number, totalCO2Kg: number }>();

    snapshot.forEach(doc => {
      const data = doc.data();
      const userId = data['collectedByUserId'];

      if (!userId) return;

      if (!resultMap.has(userId)) {
        resultMap.set(userId, { totalKg: 0, totalCO2Kg: 0 });
      }

      const userTotals = resultMap.get(userId)!;
      userTotals.totalKg += data['collectedKg'] || 0;
      userTotals.totalCO2Kg += data['savedCO2Kg'] || 0;
    });

    // arredondar
    resultMap.forEach(v => {
      v.totalKg = Number(v.totalKg.toFixed(2));
      v.totalCO2Kg = Number(v.totalCO2Kg.toFixed(2));
    });

    return resultMap;
  }

  // guardar email do utilizador
  // esta função é usada para quando um user se autenticar, através do email ou da apple, criar o user 
  // na collection 'users' e guardar o email
  async saveUserEmail(userId: string, email: string) {
    try {
      const user = doc(this.firestore, 'users', userId);
      const snap = await getDoc(user);

      if (snap.exists()) return;

      await setDoc(user, {
        email: email,
      });
    }
    catch (error) {
      console.error('Erro ao guardar email do utilizador:', error);
    }
  }

  // função para obter a organização do utilizador
  async getUserOrganization(): Promise<string> {
    try {
      if (!this.userAuthId) return '';
      const userRef = doc(this.firestore, 'users', this.userAuthId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return '';

      const userData = userSnap.data() || {};
      return userData['organization'] || '';
    } catch {
      return '';
    }
  }

  // função para obter a organização do utilizador
  async getUserRedeemKgs(): Promise<number> {
    try {
      if (!this.userAuthId) return 0;
      const userRef = doc(this.firestore, 'users', this.userAuthId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return 0;

      const userData = userSnap.data() || {};

      const collectedKg = Number(userData['collectedKg'] ?? 0);
      const redeemedKgs = Number(userData['redeemedKgs'] ?? 0);

      const result = collectedKg - redeemedKgs;

      if (result < 0) return 0

      return Math.floor(result);

    } catch {
      return 0;
    }
  }

  // atualizar redeemedKgs do utilizador
  async updateRedeemedKgs(redeemedKgs: number) {
    try {

      if (!this.userAuthId) return;
      const benefitRef = doc(this.firestore, 'users', this.userAuthId);

      await updateDoc(benefitRef, {
        redeemedKgs: increment(redeemedKgs),
      });
    }
    catch (error) {
      console.error(error);
    }
  }

  async canRedeemBenefit(benefitCost: number): Promise<boolean> {
    try {
      if (!this.userAuthId) return false;
      const userRef = doc(this.firestore, 'users', this.userAuthId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return false;

      const userData = userSnap.data() || {};

      const collectedKg = Number(userData['collectedKg'] ?? 0);
      const redeemedKgs = Number(userData['redeemedKgs'] ?? 0);

      return collectedKg - redeemedKgs >= benefitCost;

    } catch {
      return false;
    }
  }

}
